import { useEffect, type ReactNode } from "react";
import type { SiteDocument, SiteNode } from "@zirro/schema";
import { useEditorStore, saveDraftToStorage, type PageLayoutOption } from "../store";
import { TopBar } from "./TopBar";
import { LeftSidebar } from "./LeftSidebar";
import { Canvas } from "./Canvas";
import { RightInspector } from "./RightInspector";
import { MediaLibraryModal } from "./MediaLibraryModal";
import { useAutosave } from "../hooks/useAutosave";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";

export interface EditorLayoutProps {
  siteId?: string;
  initialDocument?: SiteDocument;
  onSave?: (doc: SiteDocument) => Promise<void>;
  onPreview?: () => void;
  onPublish?: () => void;
  /**
   * Live content for slot nodes on the canvas (see RenderContext.renderSlot);
   * online stores use it to show real products while designing.
   */
  renderSlot?: (node: SiteNode) => ReactNode | undefined;
  /** Extra header controls rendered on the canvas, e.g. a store's cart. */
  headerActions?: ReactNode;
  /** Alternative page designs owners can switch to from the Pages tab. */
  pageLayouts?: PageLayoutOption[];
  /**
   * Stores an uploaded photo and returns its URL. Without it uploads become
   * data URLs, which only suit local use (the schema caps image URLs at 2 KB).
   */
  onUploadImage?: (file: File) => Promise<string>;
}

export function EditorLayout({
  siteId,
  initialDocument,
  onSave,
  onPreview,
  onPublish,
  renderSlot,
  headerActions,
  pageLayouts,
  onUploadImage,
}: EditorLayoutProps) {
  const setSiteId = useEditorStore((s) => s.setSiteId);
  const setDocument = useEditorStore((s) => s.setDocument);

  useEffect(() => {
    if (siteId) {
      setSiteId(siteId);
    }
  }, [siteId, setSiteId]);

  useEffect(() => {
    if (initialDocument) {
      setDocument(initialDocument, siteId);
    }
  }, [initialDocument, siteId, setDocument]);

  const setImageUploader = useEditorStore((s) => s.setImageUploader);
  useEffect(() => {
    setImageUploader(onUploadImage ?? null);
    return () => setImageUploader(null);
  }, [onUploadImage, setImageUploader]);

  const setPageLayouts = useEditorStore((s) => s.setPageLayouts);
  useEffect(() => {
    setPageLayouts(pageLayouts ?? []);
  }, [pageLayouts, setPageLayouts]);

  useAutosave({ onSave });
  useKeyboardShortcuts();

  const handlePreviewTrigger = () => {
    const currentDoc = useEditorStore.getState().document;
    const currentSiteId = siteId ?? useEditorStore.getState().siteId;
    if (currentSiteId && currentDoc) {
      saveDraftToStorage(currentSiteId, currentDoc);
    }
    onPreview?.();
  };

  const leftWidth = useEditorStore((s) => s.leftSidebarWidth);
  const setLeftWidth = useEditorStore((s) => s.setLeftSidebarWidth);
  const leftCollapsed = useEditorStore((s) => s.leftSidebarCollapsed);

  const rightWidth = useEditorStore((s) => s.rightSidebarWidth);
  const setRightWidth = useEditorStore((s) => s.setRightSidebarWidth);
  const rightCollapsed = useEditorStore((s) => s.rightSidebarCollapsed);

  const handleLeftResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const initialWidth = leftWidth;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      setLeftWidth(Math.max(220, Math.min(600, initialWidth + delta)));
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const handleRightResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const initialWidth = rightWidth;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = startX - moveEvent.clientX;
      setRightWidth(Math.max(240, Math.min(600, initialWidth + delta)));
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  return (
    <div className="zirro-editor">
      <TopBar onPreview={onPreview ? handlePreviewTrigger : undefined} onPublish={onPublish} />
      <div className="zirro-editor__body">
        <LeftSidebar />
        {!leftCollapsed ? (
          <div
            className="zirro-resizer zirro-resizer--left"
            onMouseDown={handleLeftResizeStart}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize left sidebar"
            title="Drag to resize sidebar"
          />
        ) : null}
        <Canvas renderSlot={renderSlot} headerActions={headerActions} />
        {!rightCollapsed ? (
          <div
            className="zirro-resizer zirro-resizer--right"
            onMouseDown={handleRightResizeStart}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize right inspector"
            title="Drag to resize inspector"
          />
        ) : null}
        <RightInspector />
      </div>
      <MediaLibraryModal />
    </div>
  );
}
